import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  FormControl,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { COLORS } from "../../../assets/theme";
import { GridSearchIcon } from "@mui/x-data-grid";
import CustomTextField from "../CustomTextField";
import CustomButton from "../CustomButton";

interface ISearchHeaderProps {
  search: string;
  setSearch: (search: string) => void;
  debounceMs?: number;
  searchOnEnter?: boolean;
  buttonConfig?: {
    label?: string;
    onClick: () => void;
    minHeight?: number;
    minWidth?: number;
    [key: string]: any;
  }[];
  headerExtra?: React.ReactNode;
}

function SearchHeaderContainer({
  search,
  setSearch,
  debounceMs = 500,
  searchOnEnter = false,
  buttonConfig = [],
  headerExtra,
}: ISearchHeaderProps) {
  const [localSearch, setLocalSearch] = useState(search);
  const [isInputActive, setIsInputActive] = useState(false);
  const debounceRef = useRef<number | undefined>(undefined);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const hasPendingChange = searchOnEnter && localSearch !== (search ?? "");
  const showEnterTooltip = searchOnEnter && (hasPendingChange || isInputActive);

  // Keep input in sync when parent updates search (e.g. reset on dialog open/close)
  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  // Cleanup any pending debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalSearch(value);

    if (searchOnEnter) {
      return;
    }

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      setSearch(value);
    }, debounceMs);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (searchOnEnter && event.key === "Enter") {
      event.preventDefault();
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      setSearch(localSearch);
    }
  };

  return (
    <Box
      mb={1}
      display="flex"
      alignItems={{ xs: "stretch", sm: "flex-start" }}
      justifyContent="space-between"
      flexWrap="wrap"
      flexDirection={{ xs: "column", sm: "row" }}
      sx={{
        position: "relative",
        gap: { xs: 1.5, sm: 0 },
        width: "100%",
      }}
    >
      <FormControl
        sx={{
          flex: "1 1 100%",
          width: { xs: "100%", sm: "auto" },
          minWidth: { xs: 0, sm: 200 },
          maxWidth: { xs: "100%", sm: 400 },
          mb: { xs: 1, sm: 0 },
        }}
      >
        <Tooltip
          title="Press Enter to search"
          open={showEnterTooltip}
          placement="bottom-start"
          arrow
        >
          <span
            onMouseEnter={
              searchOnEnter ? () => setIsInputActive(true) : undefined
            }
            onMouseLeave={
              searchOnEnter ? () => setIsInputActive(false) : undefined
            }
          >
            <CustomTextField
              size={isSmallScreen ? "small" : "medium"}
              startIcon={<GridSearchIcon />}
              placeholder={searchOnEnter ? "Search (press Enter)" : "Search"}
              bgColor={COLORS.SearchFColor}
              value={localSearch}
              onChange={handleChange}
              onKeyDown={searchOnEnter ? handleKeyDown : undefined}
              onFocus={searchOnEnter ? () => setIsInputActive(true) : undefined}
              onBlur={searchOnEnter ? () => setIsInputActive(false) : undefined}
            />
          </span>
        </Tooltip>
      </FormControl>

      <Box
        display="flex"
        gap={1}
        ml={{ xs: 0, sm: 2 }}
        alignItems="center"
        sx={{
          flex: { xs: "1 1 100%", sm: "1 1 auto" },
          flexWrap: "wrap",
          rowGap: 1,
          justifyContent: { xs: "flex-start", sm: "flex-end" },
          position: "static",
          width: { xs: "100%", sm: "auto" },
        }}
      >
        {headerExtra}
        {buttonConfig.map((button, index) => (
          <CustomButton
            key={index}
            {...button}
            minHeight={button.minHeight}
            minWidth={button.minWidth}
            disabled={button.disabled}
            sx={{
              width: { xs: headerExtra ? "auto" : "100%", sm: "auto" },
              flex: {
                xs: headerExtra ? "0 0 auto" : "1 1 100%",
                sm: "0 0 auto",
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default SearchHeaderContainer;
